import { FetchResult, useMutation } from '@apollo/client';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Mutations } from '@shared/api';
import { CardEntityError } from '@shared/scenes/entity/create/hooks/useFormErrors';
import { toFieldJson } from '@shared/scenes/entity/util/toFieldJson';
import { EditableFieldRecord } from '../../types';

type UpdateMutationResult = FetchResult<MBAPI.UpdateRecurringEntitiesV2Mutation>;

// this callback accepts all of the mutation's input except for `entityFields`.
// Instead it accepts a FieldRecord which is converted to JSON entityFields
interface UpdateRecurringEntityInput extends Omit<MBAPI.UpdateRecurringEntities, 'entityFields'> {
  fields: EditableFieldRecord;
}

export interface UpdateRecurringEntityHook {
  loading: boolean;
  errors: CardEntityError[];
  updateRecurringEntity: (props: UpdateRecurringEntityInput) => Promise<UpdateMutationResult>;
}

export function useUpdateRecurringEntity(
  workflowTemplate?: MBAPI.WorkflowTemplate,
): UpdateRecurringEntityHook {
  const [updateRecurringEntityMutation, { loading, error }] = useMutation<
    MBAPI.UpdateRecurringEntitiesV2Mutation,
    MBAPI.UpdateRecurringEntitiesV2MutationVariables
  >(Mutations.entity.updateRecurringEntitiesV2);

  const [errors, setErrors] = useState<CardEntityError[]>([]);

  useEffect(() => {
    if (!isNil(error)) {
      // parse remote errors
      setErrors([{ description: error.message }]);
    }
  }, [error]);

  const updateRecurringEntity = useCallback(
    async ({ fields, ...inputProps }: UpdateRecurringEntityInput) => {
      const localErrors = validateFields(fields);
      setErrors(localErrors);
      if (!isEmpty(localErrors)) {
        return Promise.reject('Local validation');
      }

      return updateRecurringEntityMutation({
        variables: {
          input: {
            entityFields: toFieldJson(fields, workflowTemplate),
            ...inputProps,
          },
        },
      });
    },
    [updateRecurringEntityMutation, workflowTemplate],
  );

  return useMemo(() => {
    return {
      loading,
      errors,
      updateRecurringEntity,
    };
  }, [loading, errors, updateRecurringEntity]);
}

export function validateFields(fields: EditableFieldRecord): CardEntityError[] {
  return Object.entries(fields).reduce((memo: CardEntityError[], [componentId, field]) => {
    const description = field?.validate();
    if (isNil(description)) {
      return memo;
    } else {
      return [...memo, { componentId, description }];
    }
  }, []);
}
